/**
 * Phase F2 — Buyer Announcements
 * -------------------------------
 * Surfaces Phase 9 portfolio/creator announcements respecting visibility rules.
 *
 * Endpoints used (read-only):
 *   GET /api/announcements?visibility=PUBLIC|HOLDERS_ONLY|CREATOR_FOLLOWERS
 *     (Backend already filters via `_can_view_announcement` for the caller's role.)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Megaphone, Globe, Users, UserCheck, Briefcase, AlertCircle,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'PORTFOLIO', label: 'Portfolio', Icon: Briefcase, color: theme.colors.primary },
  { key: 'CREATOR', label: 'Creator', Icon: UserCheck, color: theme.colors.accent.teal },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

const VIS_META = {
  PUBLIC: { label: 'Public', color: theme.colors.status.info, Icon: Globe },
  HOLDERS_ONLY: { label: 'Holders Only', color: theme.colors.status.success, Icon: Users },
  CREATOR_FOLLOWERS: { label: 'Followers', color: theme.colors.accent.teal, Icon: UserCheck },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function classifyAnnouncement(a) {
  // Portfolio announcement: has portfolio_id. Creator announcement: no portfolio_id
  // (e.g., creator broadcast to followers) or visibility=CREATOR_FOLLOWERS.
  if (a.portfolio_id) return 'PORTFOLIO';
  if (a.visibility === 'CREATOR_FOLLOWERS') return 'CREATOR';
  return 'CREATOR';
}

const VisibilityPill = ({ visibility }) => {
  const meta = VIS_META[visibility] || { label: visibility || '—', color: theme.colors.text.muted, Icon: AlertCircle };
  const Icon = meta.Icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      <Icon size={12} /> {meta.label}
    </span>
  );
};

export default function BuyerAnnouncements() {
  const [activeTab, setActiveTab] = useState('PORTFOLIO');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/announcements?status=ACTIVE`, auth);
        if (cancelled) return;
        setRows(res.data?.announcements || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load announcements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth]);

  const counts = useMemo(() => {
    const c = { PORTFOLIO: 0, CREATOR: 0 };
    rows.forEach((a) => { c[classifyAnnouncement(a)] += 1; });
    return c;
  }, [rows]);

  const visible = useMemo(
    () => rows.filter((a) => classifyAnnouncement(a) === activeTab),
    [rows, activeTab],
  );

  return (
    <div data-testid="buyer-announcements-page" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Megaphone size={26} color={theme.colors.primary} />
          Announcements
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Updates from portfolios you hold and creators you follow. Visibility rules are enforced by the backend.
        </p>
      </div>

      {/* Tabs */}
      <div data-testid="announcements-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`announcement-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div data-testid="announcements-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading announcements…
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="announcements-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No {activeTab.toLowerCase()} announcements visible to you.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {visible.map((a) => (
            <div
              key={a.id}
              data-testid={`announcement-row-${a.id}`}
              style={{ ...cardStyle, padding: 18 }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                gap: 12, marginBottom: 8, flexWrap: 'wrap',
              }}>
                <div style={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: 16 }}>
                  {a.title || 'Announcement'}
                </div>
                <VisibilityPill visibility={a.visibility} />
              </div>
              {a.message ? (
                <div style={{ color: theme.colors.text.secondary, fontSize: 14, lineHeight: 1.5, marginBottom: 10 }}>
                  {a.message}
                </div>
              ) : null}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 12, color: theme.colors.text.muted, flexWrap: 'wrap', gap: 8,
              }}>
                <span>
                  {a.portfolio_id ? `Portfolio: ${a.portfolio_name || a.portfolio_id}` : 'Creator broadcast'}
                </span>
                <span>{fmtDate(a.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
